import React from 'react';
import { Box, Flex } from '@withjoy/joykit';
import type { ThemeJson } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import type { MonogramJson } from '@shared/websiteTemplates';
import { useColorPaletteProvider } from '../../../../ColorPaletteProvider/ColorPaletteProvider';
// eslint-disable-next-line no-restricted-imports -- imports lazy wrapper from admin
import { LazyLoadedMonogramView } from '@apps/admin/routes/WebsiteDesignerV2/routes/Layout/components/HeaderPhotoCustomization/components/MonogramEditorDialog/MonogramView.lazy';
// eslint-disable-next-line no-restricted-imports -- generated GraphQL query hook only; no admin dependencies
import { useJsonDataContainerByIdQuery } from '@website-custom-components/JsonWrapper.graphql.generated';
import { useTemplatePreviewStore } from '@apps/guest/routes/GuestSite/TemplatePreviewContext';
// 432 = 6 inches × 72 PPI (points per inch, standard screen resolution constant)
export const MONOGRAM_SIZE = 432;

type MonogramViewRendererProps = Readonly<{
  config?: ThemeJson | MonogramJson;
  showPlaceholder?: boolean;
  /** Custom monogram color; falls back to the page font color when absent. */
  fontColor?: string;
}>;

/**
 * Pure monogram renderer — draws a monogram from already-fetched JSON content.
 * Shared by the standalone `monogram` header layout and the combined
 * photo/video + monogram layouts. The guest site renders the saved (already-personalized)
 * monogram as-is — personalization happens when the design is applied to the event, not here.
 */
export const MonogramViewRenderer = ({ config, showPlaceholder, fontColor }: MonogramViewRendererProps) => {
  const { pageColor, pageFontColor } = useColorPaletteProvider();

  if (!config) {
    return showPlaceholder ? (
      <Flex
        justifyContent="center"
        alignItems="center"
        width="100%"
      >
        <Box
          width={MONOGRAM_SIZE}
          height={MONOGRAM_SIZE}
          backgroundColor="lightgray"
        />
      </Flex>
    ) : null;
  }

  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      width="100%"
    >
      <LazyLoadedMonogramView
        json={config}
        background={pageColor}
        fontColor={fontColor ?? pageFontColor}
      />
    </Flex>
  );
};

type MonogramByContainerIdProps = Readonly<{
  jsonContainerId: string | null | undefined;
  showPlaceholder?: boolean;
  /** Custom monogram color; falls back to the page font color when absent. */
  fontColor?: string;
}>;

/**
 * Fetches the monogram JSON container by id and renders it, so any background
 * layout can drop in a monogram with just a `jsonContainerId`.
 */
export const MonogramByContainerId = ({ jsonContainerId, showPlaceholder = false, fontColor }: MonogramByContainerIdProps) => {
  // Side-by-side template preview: the admin personalizes the inline monogram and ships it in the
  // preview payload, since the previewed design points at the template's unpersonalized container.
  // Outside a preview this is undefined, so the live guest site renders the saved container as usual.
  const previewMonogramContent = useTemplatePreviewStore().getActivePayload()?.previewMonogramContent;

  const { data } = useJsonDataContainerByIdQuery({
    variables: {
      payload: {
        id: jsonContainerId || '',
      },
    },
    batchMode: 'fast',
    // Client-only: the id comes from the upstream header-settings query, so running this
    // server-side forces a separate dependency-chained SSR wave. The monogram is decorative
    // header content that fills in after hydration, so SSR buys nothing here.
    ssr: false,
    skip: !jsonContainerId || !!previewMonogramContent,
  });

  const config = previewMonogramContent ?? data?.jsonDataContainerById?.content;

  return (
    <MonogramViewRenderer
      config={config}
      showPlaceholder={showPlaceholder}
      fontColor={fontColor}
    />
  );
};
